import { z } from 'zod';
import { uqcSchema } from './gst';
import { amountSchema } from './common';
import { patchOf } from './zod-setup';
/** The catalogue: things you sell, and things you buy. */
export const ITEM_TYPES = ['goods', 'service'];
export const createItemSchema = z
    .object({
    name: z.string().trim().min(1, 'Give the item a name').max(200),
    sku: z.string().trim().max(60).optional(),
    type: z.enum(ITEM_TYPES).default('service'),
    description: z.string().trim().max(2000).optional(),
    /** Default selling price. A line may still override it. */
    rate: amountSchema.optional(),
    unit: z.string().trim().max(30).optional(),
    /**
     * The GST department's own unit code, from a fixed list. `unit` is free
     * text and holds things like "project"; the portal will not accept that,
     * so Table 12 reports THIS. Left unset it is inferred from `unit`, which
     * falls back to OTH rather than guessing.
     */
    uqc: uqcSchema.optional(),
    taxId: z.string().trim().max(36).nullable().optional(),
    /**
     * HSN for goods, SAC for services. Required on GST invoices above the
     * turnover threshold, and the field an auditor checks first.
     */
    hsnSac: z.string().trim().max(20).optional(),
    /**
     * What kind of revenue selling this is - the default behind the document's
     * default.
     *
     * The column existed from migration 0010 and was written by nobody and read
     * by nobody, which made the resolution order it was designed for a fiction.
     * A line built from this item starts here; the composer's document-level
     * choice still wins over it, and the line's own choice wins over both.
     */
    incomeCategoryId: z.string().trim().max(36).nullable().optional(),
    /** What it costs you, for margin reporting and for defaulting bill lines. */
    purchaseRate: amountSchema.optional(),
    purchaseDescription: z.string().trim().max(2000).optional(),
    isActive: z.boolean().default(true),
    customFields: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
export const updateItemSchema = patchOf(createItemSchema).strict();
/**
 * 'active' or 'draft' on the row (migration 0017).
 *
 * A draft was PROPOSED - by an agent reading a rate card - and no person has
 * confirmed it. It is in no picker and on no invoice line until they do.
 */
export const CATALOGUE_STATUSES = ['active', 'draft'];
/**
 * Which rows a list shows. `active` (the default) is confirmed and not
 * archived - what a picker wants. `inactive` is confirmed and archived.
 * `draft` is proposals waiting in the Inbox, and `all` is every row, drafts
 * included, each carrying its `status`. A draft is never in a list that did
 * not ask for it by one of the last two: every reader filters on the
 * included side.
 */
export const LIST_STATUSES = ['active', 'inactive', 'draft', 'all'];
export const listItemsQuerySchema = z
    .object({
    q: z.string().trim().max(200).optional(),
    type: z.enum([...ITEM_TYPES, 'all']).default('all'),
    status: z.enum(LIST_STATUSES).default('active'),
    cursor: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict();
/**
 * What has been billed free-hand and is not in the catalogue yet.
 *
 * Capped low on purpose: this is a suggestion list somebody scans, not a
 * report. Twenty rows is already more than anyone promotes in one sitting.
 */
export const recentlyBilledQuerySchema = z
    .object({
    limit: z.coerce.number().int().min(1).max(50).default(20),
})
    .strict();
// ── Taxes ─────────────────────────────────────────────────────────────────
export const createTaxSchema = z
    .object({
    name: z.string().trim().min(1, 'Name the tax').max(60),
    /** 18 means 18%. Three decimal places, which covers every real rate. */
    percentage: z.number().min(0).max(100),
    /**
     * `group` applies several rates at once and is how a non-GST org models
     * something like "GST 18% = CGST 9% + SGST 9%" by hand. Under the GST
     * switch the split is computed instead, so groups are rarely needed there.
     */
    type: z.enum(['simple', 'group']).default('simple'),
    componentTaxIds: z.array(z.string().trim().max(36)).max(10).optional(),
    isCompound: z.boolean().default(false),
    isActive: z.boolean().default(true),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.type === 'group' && (!v.componentTaxIds || v.componentTaxIds.length < 2)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['componentTaxIds'],
            message: 'A tax group needs at least two component rates.',
        });
    }
});
export const updateTaxSchema = z
    .object({
    name: z.string().trim().min(1).max(60).optional(),
    percentage: z.number().min(0).max(100).optional(),
    isActive: z.boolean().optional(),
})
    .strict();
/**
 * The GST rates every Indian org starts with.
 *
 * Seeded rather than hardcoded, so an org can retire the ones it never uses and
 * a rate change does not need a deploy. 0% is included on purpose: exempt and
 * nil-rated supplies still have to appear on a return, and a line with no tax
 * at all is a different thing from a line taxed at nothing.
 */
export const DEFAULT_GST_RATES = [
    { name: 'GST 0%', percentage: 0 },
    { name: 'GST 5%', percentage: 5 },
    { name: 'GST 12%', percentage: 12 },
    { name: 'GST 18%', percentage: 18 },
    { name: 'GST 28%', percentage: 28 },
];
/**
 * Codes for the items that have none, in one press (see `hsn-sac.ts`).
 *
 * `fillLines` also writes the code onto every document line drawn from the
 * item that has none - the snapshot rule is that a line never re-reads its
 * item, so a code set on the item today reaches the March invoices only by
 * being written there. It changes no figure, which is why an issued invoice
 * may take it; it changes what GSTR-1 Table 12 says, which is why it needs
 * `invoice.manage` and is audited.
 */
export const applyHsnSchema = z
    .object({
    assignments: z
        .array(z.object({
        itemId: z.string().min(1),
        hsnSac: z.string().trim().min(4, 'An HSN or SAC is at least four digits').max(20),
    }))
        .min(1)
        .max(200),
    fillLines: z.boolean().default(false),
})
    .strict();
//# sourceMappingURL=items.js.map